import { useMemo } from "react";

import {
  ArchiveBoxArrowDownIcon,
  BoltIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  InboxArrowDownIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import HeadlineStat from "../shared/HeadlineStat";
import classNames from "../../utils/classNames";
import useVehicleStats from "../../data/useVehicleStats";
import { PartsStatusDropdowns, PartsStatusValues } from "../../models/Vehicle";

/**
 * Colour hint per parts-status bucket so the breakdown list mirrors the
 * tinted carrier rows on the orders page. Keys map to the `PartsStatusDropdowns`
 * enum names. Anything unmatched falls through to the neutral primary tone.
 */
const STATUS_TONE: Record<
  string,
  { bar: string; dot: string; text: string }
> = {
  "Awaiting Schedule": {
    bar: "bg-cyan-400",
    dot: "bg-cyan-500",
    text: "text-cyan-700",
  },
  "Parts - Engine + Gearbox": {
    bar: "bg-emerald-400",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
  },
  "Parts - No Engine": {
    bar: "bg-teal-400",
    dot: "bg-teal-500",
    text: "text-teal-700",
  },
  "Parts + Export": {
    bar: "bg-violet-400",
    dot: "bg-violet-500",
    text: "text-violet-700",
  },
  "Export - Engine": {
    bar: "bg-blue-400",
    dot: "bg-blue-500",
    text: "text-blue-700",
  },
  "Export - Frontend": {
    bar: "bg-sky-400",
    dot: "bg-sky-500",
    text: "text-sky-700",
  },
  "Export - Engine + Frontend": {
    bar: "bg-indigo-400",
    dot: "bg-indigo-500",
    text: "text-indigo-700",
  },
  Salvage: {
    bar: "bg-amber-400",
    dot: "bg-amber-500",
    text: "text-amber-700",
  },
  Scrap: {
    bar: "bg-rose-400",
    dot: "bg-rose-500",
    text: "text-rose-700",
  },
  Cancelled: {
    bar: "bg-slate-400",
    dot: "bg-slate-500",
    text: "text-slate-700",
  },
};

const FALLBACK_TONE = {
  bar: "bg-primary-400",
  dot: "bg-primary-500",
  text: "text-primary-700",
};

const fmt = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString("en-GB");

export default function VehicleStats() {
  const { stats } = useVehicleStats();

  const data = useMemo(() => {
    if (stats.isLoading || !stats.data) return undefined;
    return stats.data.data;
  }, [stats.isLoading, stats.data]);

  const isLoading = stats.isLoading;

  // Buckets are a fixed client-side enum. We look each one up in the
  // server response so the UI still renders a full list even when a
  // bucket hasn't yet occurred today.
  const statusLabels = Object.values(PartsStatusDropdowns).filter(
    (ct) => isNaN(Number(ct))
  ) as string[];

  const statusRows = statusLabels.map((label) => {
    const enumVal = PartsStatusValues[label as keyof typeof PartsStatusValues];
    const count =
      data?.engineExportCounts?.find((c) => c.status === enumVal)?.count ?? 0;
    return { label, count };
  });

  const totalStatus = statusRows.reduce((sum, r) => sum + r.count, 0);
  const maxStatus = statusRows.reduce((m, r) => Math.max(m, r.count), 0);

  return (
    <Card
      title="Vehicles overview"
      subtitle="Pipeline across collection, dismantling and export"
      bodyClassName="p-4 space-y-4"
      collapsible
    >
      {/* Headline stats — first row. Responsive grid matches the orders &
          QC pages so jumps between them feel consistent. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <HeadlineStat
          value={fmt(data?.awaitingCollection)}
          label="Awaiting collection"
          sublabel={`${fmt(data?.awaitingCollectionToday)} today`}
          icon={TruckIcon}
          tone="amber"
        />
        <HeadlineStat
          value={fmt(data?.collectedToday)}
          label="Collected today"
          sublabel="Arrived on site"
          icon={CheckCircleIcon}
          tone="emerald"
        />
        <HeadlineStat
          value={fmt(data?.awaitingDropOff)}
          label="Awaiting drop off"
          sublabel={`${fmt(data?.awaitingDropOffToday)} today`}
          icon={ArchiveBoxArrowDownIcon}
          tone="rose"
        />
        <HeadlineStat
          value={fmt(data?.awaitingInventory)}
          label="Awaiting schedule"
          sublabel="Ready to plan"
          icon={CalendarDaysIcon}
          tone="cyan"
        />
        <HeadlineStat
          value={fmt(data?.awaitingDismantling)}
          label="Awaiting dismantling"
          sublabel="Scheduled, not started"
          icon={WrenchScrewdriverIcon}
          tone="violet"
        />
        <HeadlineStat
          value={fmt(data?.awaitingCOD)}
          label="Awaiting COD"
          sublabel="Of priced vehicles"
          icon={ClipboardDocumentCheckIcon}
          tone="yellow"
        />
      </div>

      {/* Secondary row — lower-frequency but still useful. Same tile
          component, but we mark it `compact` to cram the info without
          making the page taller. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <HeadlineStat
          compact
          value={fmt(data?.addedToday)}
          label="Added today"
          icon={InboxArrowDownIcon}
          tone="blue"
        />
        <HeadlineStat
          compact
          value={fmt(data?.arrivingToday)}
          label="Arriving today"
          icon={TruckIcon}
          tone="cyan"
        />
        <HeadlineStat
          compact
          value={fmt(data?.arrivingTomorrow)}
          label="Arriving tomorrow"
          icon={TruckIcon}
          tone="slate"
        />
        <HeadlineStat
          compact
          value={fmt(data?.startsAndDrives)}
          label="Starts & drives"
          sublabel="On yard"
          icon={BoltIcon}
          tone="emerald"
        />
      </div>

      {/* Parts status breakdown — mirrors the shipping carriers panel on
          the orders page so the two dashboards share visual vocabulary. */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <header className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Parts status breakdown
              </h3>
              <p className="text-[11px] text-slate-500">
                Vehicles grouped by dismantling destination
              </p>
            </div>
          </div>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            {fmt(totalStatus)}
          </span>
        </header>

        {isLoading && totalStatus === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
            Loading statuses…
          </div>
        ) : totalStatus === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
            No vehicles in any parts bucket.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
            {statusRows.map(({ label, count }) => {
              const tone = STATUS_TONE[label] ?? FALLBACK_TONE;
              const pct = maxStatus === 0 ? 0 : (count / maxStatus) * 100;
              const share =
                totalStatus === 0
                  ? 0
                  : Math.round((count / totalStatus) * 100);
              return (
                <li key={label} className="space-y-1">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span
                        className={classNames(
                          "h-1.5 w-1.5 flex-none rounded-full",
                          tone.dot
                        )}
                        aria-hidden
                      />
                      <span className="truncate font-medium text-slate-700">
                        {label}
                      </span>
                    </span>
                    <span className="flex flex-none items-baseline gap-1 tabular-nums">
                      <span className="font-semibold text-slate-900">
                        {fmt(count)}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {share}%
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={classNames(
                        "h-full rounded-full transition-[width] duration-500",
                        tone.bar
                      )}
                      style={{
                        width: `${count === 0 ? 0 : Math.max(pct, 4)}%`,
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </Card>
  );
}
